// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

/*
 This module serves as a global collection of utility functions.
 They somehow define the "coding style" of the project.
 Do not put stack or frontend specific code in here. This module shall only contain
 generic code which can be used in any part of the project.
*/

export function isSet<T>(value: T | undefined | null): value is T {
  return value !== undefined && value !== null;
}

export function isNum(value: number | undefined | null): value is number {
  return isSet(value) && !isNaN(value);
}

// round the given number to max. number of decimals
export function round(num: number | undefined, digits?: number, scaleFactor?: number) {
  if (isSet(num) && !isNaN(num)) {
    const shownDigits = isSet(digits) ? digits : 2;
    const digitsExp = 10 ** shownDigits;
    const usedScaleFactor = isSet(scaleFactor) ? scaleFactor : 1;
    // determine whether we need to round
    return Math.round(num * usedScaleFactor * digitsExp) / digitsExp;
  }
  return NaN; // must not happen
}

// round the given number to exactly number of decimals
export function roundFix(num: number | undefined, digits?: number, scaleFactor?: number) {
  if (isSet(num) && !isNaN(num)) {
    const shownDigits = isSet(digits) ? digits : 2;
    const usedScaleFactor = isSet(scaleFactor) ? scaleFactor : 1;
    // determine whether we need to round
    return (num * usedScaleFactor).toFixed(shownDigits);
  }
  return "";
}

// round to x significant digits; keep integer if larger than significant digits
export function roundToDigits(num: number | undefined | null | string, digits?: number) {
  const parsed = typeof num === "string" ? parseFloat(num) : num;
  if (!isSet(parsed) || isNaN(parsed as any)) {
    return num;
  }
  // check for float underflow
  if (Math.abs(parsed) < 1e-10) {
    return 0;
  }
  const shownDigits = isSet(digits) ? digits : 2;
  const digitsExp = 10 ** shownDigits;
  // check if number is bigger than the digits to show; then we keep the whole number
  if (Math.abs(parsed) >= digitsExp) {
    return Math.round(parsed);
  }
  return parseFloat(parsed.toPrecision(shownDigits));
}

// expects timestamp in seconds, returns formatted time string
export function formatTimestamp(timestamp: number) {
  if (isNaN(timestamp)) {
    return "";
  }
  if (timestamp < 3600) {
    return `${`0${Math.floor(timestamp / 60)}`.slice(-2)}:${`0${timestamp % 60}`.slice(-2)}`;
  }
  if (timestamp < 24 * 3600) {
    return `${`0${Math.floor(timestamp / 3600)}`.slice(-2)}:${`0${Math.floor(
      (timestamp % 3600) / 60,
    )}`.slice(-2)}:${`0${timestamp % 60}`.slice(-2)}`;
  }
  return `${Math.floor(timestamp / (24 * 3600))}T ${`0${Math.floor(
    (timestamp % (24 * 3600)) / 3600,
  )}`.slice(-2)}:${`0${Math.floor((timestamp % 3600) / 60)}`.slice(-2)}:${`0${
    timestamp % 60
  }`.slice(-2)}`;
}
